import { Link } from "@tanstack/react-router";
import { Download, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { usePortalRuntime, type PortalProblemRecord } from "@/lib/portal-runtime";
import {
  formatRecordCost,
  problemRecordsCsv,
  selectProblemRecords,
  type RecordSort,
} from "@/lib/problem-record-presentation";

export function ProblemRecordTable({ records }: { records: readonly PortalProblemRecord[] }) {
  const { mode } = usePortalRuntime();
  const governedExportConnected = false;
  const exportAllowed = mode !== "live" || governedExportConnected;
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("ALL");
  const [severity, setSeverity] = useState("ALL");
  const [sort, setSort] = useState<RecordSort>("updated");
  const rows = useMemo(
    () => selectProblemRecords(records, query, status, severity, sort),
    [records, query, status, severity, sort],
  );
  function download() {
    const url = URL.createObjectURL(
      new Blob([problemRecordsCsv(rows)], { type: "text/csv;charset=utf-8" }),
    );
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "broadview-problem-records.csv";
    anchor.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
  return (
    <section className="rounded-lg border border-border bg-card panel-glow">
      <div className="no-print flex flex-wrap items-end gap-3 border-b border-border p-4">
        <label className="min-w-0 flex-1 text-xs text-muted-foreground">
          <span className="mb-1.5 block">Search Problem Records</span>
          <span className="flex items-center gap-2 rounded border border-input bg-elevated px-3 py-2">
            <Search className="size-4 shrink-0" aria-hidden="true" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Code, title, description, owner…"
              className="min-w-0 w-full bg-transparent text-sm text-foreground outline-none"
            />
          </span>
        </label>
        <label className="text-xs text-muted-foreground">
          <span className="mb-1.5 block">Status</span>
          <select
            aria-label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="h-10 rounded border border-input bg-elevated px-2 text-sm text-foreground"
          >
            <option value="ALL">All statuses</option>
            {["DRAFT", "OPEN", "ON_HOLD", "CLOSED"].map((value) => (
              <option key={value} value={value}>
                {value.replaceAll("_", " ")}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs text-muted-foreground">
          <span className="mb-1.5 block">Severity</span>
          <select
            aria-label="Severity"
            value={severity}
            onChange={(e) => setSeverity(e.target.value)}
            className="h-10 rounded border border-input bg-elevated px-2 text-sm text-foreground"
          >
            <option value="ALL">All severities</option>
            {["UNASSESSED", "LOW", "MEDIUM", "HIGH", "CRITICAL"].map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs text-muted-foreground">
          <span className="mb-1.5 block">Sort records</span>
          <select
            aria-label="Sort records"
            value={sort}
            onChange={(e) => setSort(e.target.value as RecordSort)}
            className="h-10 rounded border border-input bg-elevated px-2 text-sm text-foreground"
          >
            <option value="updated">Recently updated</option>
            <option value="claims">Most linked claims</option>
            <option value="severity">Highest assessed severity</option>
            <option value="code">Record code</option>
          </select>
        </label>
        <button
          type="button"
          disabled={!rows.length || !exportAllowed}
          onClick={download}
          title={
            exportAllowed
              ? "Export the currently visible records"
              : "Controlled export is unavailable until the governed export service is connected."
          }
          className="flex h-10 items-center gap-2 rounded border border-border px-3 text-xs font-medium hover:bg-elevated disabled:opacity-50"
        >
          <Download className="size-3.5" aria-hidden="true" />
          {exportAllowed ? "Export CSV" : "Export unavailable"}
        </button>
      </div>
      <p className="px-4 py-3 text-xs text-muted-foreground" role="status">
        {rows.length} of {records.length} records · costs are source-reported, not forecast exposure
        {!exportAllowed
          ? " · live CSV export is disabled until the governed export.create/audit path is connected"
          : ""}
      </p>
      <div
        tabIndex={0}
        role="region"
        aria-label="Problem Record table, scroll horizontally for all columns"
        className="overflow-x-auto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <table className="w-full min-w-[850px] text-sm">
          <caption className="sr-only">Verified Problem Records for the selected Division</caption>
          <thead className="border-y border-border bg-elevated/50 text-left text-[11px] uppercase tracking-wider text-muted-foreground">
            <tr>
              {[
                "Record",
                "Status / severity",
                "Linked claims",
                "Source-reported cost",
                "Owner",
                "Updated",
              ].map((heading) => (
                <th key={heading} scope="col" className="px-4 py-3 font-medium">
                  {heading}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border/70">
            {rows.map((record) => (
              <tr key={record.id} className="hover:bg-elevated/50">
                <th scope="row" className="max-w-[360px] px-4 py-3 text-left font-normal">
                  <Link
                    to="/problem-record/$id"
                    params={{ id: record.id }}
                    className="font-medium hover:text-primary"
                  >
                    <span className="num block text-xs text-primary">{record.displayId}</span>
                    <span className="mt-1 block">{record.title}</span>
                  </Link>
                  <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                    {record.summary || "No summary supplied."}
                  </p>
                  {record.classification === "HISTORICAL_REAL" && (
                    <p className="mt-1 text-[11px] text-primary">
                      Historical real data
                      {record.dataCutoffAt
                        ? ` · through ${new Date(record.dataCutoffAt).toLocaleDateString()}`
                        : ""}
                    </p>
                  )}
                </th>
                <td className="px-4 py-3">
                  <span className="block text-xs text-primary">
                    {record.status === "DRAFT"
                      ? "DRAFT REVIEW"
                      : record.status.replaceAll("_", " ")}
                  </span>
                  <span className="mt-1 block text-[11px] text-muted-foreground">
                    {record.severity}
                  </span>
                </td>
                <td className="num px-4 py-3">
                  {record.claimCount?.toLocaleString() ?? "Unavailable"}
                </td>
                <td className="num px-4 py-3">{formatRecordCost(record)}</td>
                <td className="max-w-[180px] break-words px-4 py-3 text-xs text-muted-foreground">
                  {record.ownerUserId ?? "Unassigned"}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-xs text-muted-foreground">
                  {new Date(record.updatedAt).toLocaleDateString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length === 0 && (
        <p className="p-8 text-center text-sm text-muted-foreground">
          No records match these filters.
        </p>
      )}
    </section>
  );
}
